import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, Check, Tag } from 'lucide-react';
import { getPublishedVendorDiscountCodes } from '../../services/discountCodeService';

const formatOffer = (row) => {
  if (row.discount_type === 'percentage') {
    return `${Number(row.discount_value)}% off`;
  }
  return `$${Number(row.discount_value).toFixed(2)} off`;
};

const formatDateRange = (from, until) => {
  const fmt = (v) => {
    if (!v) return null;
    return new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const a = fmt(from);
  const b = fmt(until);
  if (!a && !b) return null;
  if (a && b) return `${a} – ${b}`;
  if (b) return `Until ${b}`;
  if (a) return `From ${a}`;
  return null;
};

const VendorPublicDiscountCodes = ({ vendorId, toast }) => {
  const [copiedId, setCopiedId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['published-vendor-discount-codes', vendorId],
    queryFn: () => getPublishedVendorDiscountCodes(vendorId),
    enabled: !!vendorId,
    staleTime: 2 * 60 * 1000,
  });

  const codes = data?.data ?? [];

  const copy = async (row) => {
    try {
      await navigator.clipboard.writeText(row.code);
      setCopiedId(row.id);
      setTimeout(() => setCopiedId(null), 2000);
      toast?.success?.('Copied', 'Discount code copied to clipboard.');
    } catch {
      toast?.error?.('Copy failed', 'Select and copy the code manually.');
    }
  };

  if (!vendorId || isLoading || codes.length === 0) {
    return null;
  }

  return (
    <div className="pb-6 border-b border-gray-200">
      <div className="flex items-center gap-2 mb-4">
        <Tag className="w-5 h-5 text-gray-700" aria-hidden />
        <h2 className="text-lg sm:text-xl font-light text-black">
          Special offers
        </h2>
      </div>
      <p className="text-sm text-gray-600 font-light mb-4">
        Use these codes at checkout when you book this vendor.
      </p>
      <div className="space-y-3">
        {codes.map((row) => {
          const range = formatDateRange(row.valid_from, row.valid_until);
          return (
            <div
              key={row.id}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-gray-200 rounded-xl p-4 bg-gray-50/50"
            >
              <div className="min-w-0">
                <div className="font-mono text-lg font-medium tracking-wide text-black">
                  {row.code}
                </div>
                <div className="text-sm text-gray-700 mt-1 font-light">{formatOffer(row)}</div>
                {range && (
                  <div className="text-xs text-gray-500 mt-1 font-light">{range}</div>
                )}
                {row.description && (
                  <p className="text-sm text-gray-600 mt-2 font-light whitespace-pre-line">
                    {row.description}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => copy(row)}
                className="inline-flex items-center justify-center gap-2 shrink-0 px-4 py-2.5 bg-black text-white rounded-lg text-sm font-light hover:bg-gray-800 transition-colors touch-manipulation"
              >
                {copiedId === row.id ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy code
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VendorPublicDiscountCodes;
